import { AlertTriangle, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DAY = 86400000;
const ITEMS = [
  {
    key: "boc3", tid: "boc3", title: "BOC-3 process agent filing",
    url: "https://www.fmcsa.dot.gov/registration/form-boc-3-designation-agents-service-process", linkLabel: "View BOC-3 on FMCSA",
    text: "A process agent company files it for you. FMCSA won't activate your authority without it.",
  },
  {
    key: "bmc91", tid: "bmc91", title: "BMC-91 insurance filing",
    url: "https://www.fmcsa.dot.gov/registration/insurance-requirements", linkLabel: "View insurance rules",
    text: "Your insurance company files proof of liability coverage (BMC-91 or 91X) directly with FMCSA.",
  },
];

function deadlineInfo(issued) {
  if (!issued) return null;
  const due = new Date(new Date(`${issued}T00:00:00`).getTime() + 90 * DAY);
  const today = new Date(new Date().toDateString());
  return { due, left: Math.round((due - today) / DAY) };
}

function Countdown({ info, tid }) {
  if (!info) return null;
  const urgent = info.left <= 14;
  return (
    <p className={`text-sm font-semibold mt-1 ${urgent ? "text-[#F87171]" : "text-[#FFD600]"}`} data-testid={`${tid}-deadline`}>
      {info.left >= 0
        ? `${info.left} day${info.left === 1 ? "" : "s"} left. Deadline: ${info.due.toLocaleDateString()}`
        : `Deadline passed on ${info.due.toLocaleDateString()}. File immediately and check your application status with FMCSA.`}
    </p>
  );
}

function ItemRow({ item, info, onFiled }) {
  return (
    <div className="border border-[#262626] bg-[#0A0A0A]/60 p-3 sm:p-4" data-testid={`${item.tid}-row`}>
      <div className="font-heading font-bold">{item.title}</div>
      <p className="text-xs text-muted-foreground mt-0.5">{item.text}</p>
      <Countdown info={info} tid={item.tid} />
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <a href={item.url} target="_blank" rel="noopener noreferrer" data-testid={`${item.tid}-alert-link`}
          className="cta-primary min-h-[44px] px-4 font-mono-label text-[11px] inline-flex items-center justify-center gap-2">
          <ExternalLink size={13} /> {item.linkLabel}
        </a>
        <button onClick={onFiled} data-testid={`${item.tid}-mark-filed`}
          className="min-h-[44px] px-4 border border-[#262626] hover:border-[#16A34A] hover:text-[#4ADE80] font-mono-label text-[11px] inline-flex items-center justify-center gap-2 transition-colors">
          <CheckCircle2 size={13} /> Mark as filed
        </button>
      </div>
    </div>
  );
}

export default function Boc3Alert({ profile, api, onChanged, guides }) {
  const byKey = Object.fromEntries((profile.filings || []).map((f) => [f.key, f]));
  const pending = ITEMS.filter((it) => byKey[it.key] && byKey[it.key].status !== "completed" && guides?.[it.key]?.status !== "not_required");
  if (!pending.length) return null;
  const info = deadlineInfo(profile.mc_issued_date);
  const urgent = info && info.left <= 14;
  const save = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      onChanged();
    } catch {
      toast.error("Could not save. Please try again.");
    }
  };
  const markFiled = (it) => save(() => api.put(`/filings/${it.key}`, { status: "completed", value: byKey[it.key].value }), `${it.title} marked as filed`);
  const tone = urgent ? "border-[#DC2626] bg-[#DC2626]/10" : "border-[#F59E0B] bg-[#F59E0B]/10";
  return (
    <section className={`border-2 ${tone} p-4 sm:p-5 mb-8`} role="alert" data-testid="boc3-alert">
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className={`shrink-0 mt-0.5 ${urgent ? "text-[#F87171]" : "text-[#F59E0B]"}`} />
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold text-lg" data-testid="mc-deadline-title">
            {pending.length > 1 ? "File your BOC-3 and insurance within 90 days of receiving your MC#" : `File your ${pending[0].key === "boc3" ? "BOC-3" : "BMC-91 insurance"} within 90 days of receiving your MC#`}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            If these aren't on file with FMCSA within 90 days, your operating authority application can be dismissed and you'll have to reapply.
          </p>
          <label className="mt-4 block text-xs text-muted-foreground">
            Date you received your MC#
            <input type="date" value={profile.mc_issued_date || ""} max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => save(() => api.put("/profile", { mc_issued_date: e.target.value || null }))} data-testid="boc3-mc-date"
              className="mt-1 block h-11 px-3 bg-[#141414] border border-[#262626] text-white text-sm [color-scheme:dark]" />
          </label>
          <div className={`mt-4 grid grid-cols-1 ${pending.length > 1 ? "lg:grid-cols-2" : ""} gap-3`}>
            {pending.map((it) => <ItemRow key={it.key} item={it} info={info} onFiled={() => markFiled(it)} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
